import { Section } from '@/components/shared/Content';
import { Box, Flex, font, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import React from 'react';
import styled from 'styled-components';
import { SectionSubtitle } from '../pet/StyledElements';
import { type ImpactSlideProps, ImpactSlider } from './ImpactCards';
import { GBSectionParagraph, GBSectionTitle } from './StyledElements';

const ContentWrapper = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const OurImpactSectionTitle = styled(GBSectionTitle)`
  text-align: center !important;
`;

const OurImpactSectionParagraph = styled(GBSectionParagraph)`
  text-align: center;
  font-size: 1.8rem;
  font-weight: 700;
`;

const OurImpactSectionSubtitle = styled(SectionSubtitle)`
  margin: 8px auto 0 auto;
  font-size: 1.8rem;

  ${MEDIA_QUERIES.mobile`
    padding: 0 10px;
  `}
`;

const StyledLinkContainer = styled.ul`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  justify-content: center;
  align-items: center;
  gap: 20px 33px;
  margin: 0 auto;
  ${font('main', { fontSize: '1.6rem', lineHeight: '2.2rem' })};

  ${MEDIA_QUERIES.mobile`
    li:nth-child(4n) a:after {
      display: none;
    }
  `}

  ${MEDIA_QUERIES.tabletAndDesktop`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 33px;
    margin: 0 auto;
    ${font('main', { fontSize: '1.6rem', lineHeight: '2.24rem' })};
  `}

  li a {
    color: ${themedColor('primary')};
    display: block;
    position: relative;

    &:after {
      content: '';
      display: block;
      width: 1px;
      height: 15px;
      position: absolute;
      right: -18px;
      top: 4px;
      background: ${themedColor('veryLightGrey')};
    }

    &[data-last='true']:after {
      display: none;
    }
  }
`;

type Year = {
  url: string;
  year: string;
};

export interface OurImpact2024Props {
  title: string;
  subtitle: string;
  years: Year[];
  charities: ImpactSlideProps[];
}

export const OurImpact2024: React.FC<OurImpact2024Props> = ({ title, subtitle, years = [], charities = [] }) => {
  return (
    <Section py={[40, 70]} darkBackground>
      <ContentWrapper width={1}>
        <Box>
          <OurImpactSectionTitle tabIndex={0}>{title}</OurImpactSectionTitle>
          <OurImpactSectionSubtitle tabIndex={0}>{subtitle}</OurImpactSectionSubtitle>
        </Box>

        <ImpactSlider charities={charities} />

        <Box mb={16}>
          <OurImpactSectionParagraph>Previous years</OurImpactSectionParagraph>
        </Box>
        <StyledLinkContainer role="list" aria-label="Previous years">
          {years.map(({ year, url }, index) => (
            <li key={year}>
              <a href={url} target="_blank" rel="noreferrer" data-last={index === years.length - 1}>
                {year}
              </a>
            </li>
          ))}
        </StyledLinkContainer>
      </ContentWrapper>
    </Section>
  );
};
